import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { ArrowRight, Users, Share2, BarChart3, Shield, Check, Zap, Calendar, Play, Pause, Volume2, VolumeX, Video, MessageCircle, MapPin } from 'lucide-react';
import { LogoWhite } from '@/components/Logo';
import api from '@/lib/api';
import logger from '@/lib/logger';
import { motion } from 'framer-motion';
import { Seo } from '@/components/Seo';
import { FaqSection } from '@/components/landing/FaqSection';
import { PricingTiers } from '@/components/marketing/PricingTiers';
import { SeatCalculator } from '@/components/marketing/SeatCalculator';

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } };
const fadeIn = { hidden: { opacity: 0 }, visible: { opacity: 1 } };
const scaleUp = { hidden: { opacity: 0, scale: 0.95 }, visible: { opacity: 1, scale: 1 } };

const AB_VARIANTS = {
  A: { cta: 'Start for $35/month', subtext: 'Already a member?' },
  B: { cta: 'Try Free for 7 Days', subtext: 'No credit card required' },
};

function useABVariant() {
  const [variant, setVariant] = useState(null);
  const tracked = useRef(false);
  useEffect(() => {
    let v = localStorage.getItem('ab_hero_cta');
    if (!v || !AB_VARIANTS[v]) {
      v = Math.random() < 0.5 ? 'A' : 'B';
      localStorage.setItem('ab_hero_cta', v);
    }
    setVariant(v);
    if (!tracked.current) {
      tracked.current = true;
      api.trackABEvent('hero_cta', v, 'impression').catch(err => logger.error(err));
    }
  }, []);
  return variant;
}

function DemoVideoPlayer() {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);

  const toggle = () => {
    if (!videoRef.current) return;
    if (playing) { videoRef.current.pause(); }
    else { videoRef.current.play(); }
    setPlaying(!playing);
  };

  return (
    <div className="relative rounded-2xl overflow-hidden shadow-[0_0_60px_rgba(0,122,255,0.12)] border border-white/10 group cursor-pointer" onClick={toggle} data-testid="demo-video-player">
      <video
        ref={videoRef}
        className="w-full aspect-video object-cover"
        src="/video_product_demo.mp4"
        poster="https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1280&q=80"
        playsInline
        onEnded={() => setPlaying(false)}
      />
      <div className={`absolute inset-0 flex items-center justify-center transition-opacity duration-300 ${playing ? 'opacity-0 group-hover:opacity-100' : 'opacity-100'} bg-black/30`}>
        <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center border border-white/30 hover:scale-110 transition-transform" data-testid="demo-play-btn">
          {playing ? <Pause className="w-6 h-6 text-white" /> : <Play className="w-6 h-6 text-white ml-1" />}
        </div>
      </div>
    </div>
  );
}

function TestimonialVideoPlayer() {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);

  const toggle = () => {
    if (!videoRef.current) return;
    if (playing) { videoRef.current.pause(); }
    else { videoRef.current.play(); }
    setPlaying(!playing);
  };

  return (
    <div className="relative rounded-2xl overflow-hidden border border-white/10 group cursor-pointer" onClick={toggle} data-testid="testimonial-video-player">
      <video
        ref={videoRef}
        className="w-full aspect-video object-cover"
        src="/video_testimonial.mp4"
        poster="https://images.unsplash.com/photo-1573497161161-c3e73707e25c?w=800&q=80"
        playsInline
        onEnded={() => setPlaying(false)}
      />
      <div className={`absolute inset-0 flex items-center justify-center transition-opacity duration-300 ${playing ? 'opacity-0 group-hover:opacity-100' : 'opacity-100'} bg-black/20`}>
        <div className="w-14 h-14 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center border border-white/30 hover:scale-110 transition-transform" data-testid="testimonial-play-btn">
          {playing ? <Pause className="w-5 h-5 text-white" /> : <Play className="w-5 h-5 text-white ml-0.5" />}
        </div>
      </div>
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent p-6 pointer-events-none">
        <p className="text-white font-semibold text-lg mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Priya Desai</p>
        <p className="text-zinc-400 text-sm">Director of Sales, BrightPath Consulting</p>
      </div>
    </div>
  );
}

const FEATURES = [
  { icon: Users, title: 'Lead Management', desc: 'Create, organize, and track leads with full contact details, categories, and custom tags.', span: 'md:col-span-2' },
  { icon: Share2, title: 'Lead Sharing', desc: 'Share leads with team members instantly. Collaborate on high-value opportunities together.', span: '' },
  { icon: Video, title: '1:1 Video Calls', desc: 'Jump from chat to a face-to-face WebRTC video call in one click — no plugins, no scheduling.', span: '' },
  { icon: MessageCircle, title: 'Team Messaging', desc: 'Real-time DMs with typing indicators, read receipts, attachments, emojis, and per-message reactions.', span: '' },
  { icon: MapPin, title: 'Smart Addresses', desc: 'Google Places autocomplete on every lead — type a few letters, get Address, City, State, ZIP auto-filled, plus a satellite map and one-click directions.', span: '' },
  { icon: Zap, title: 'One-Click Prospecting', desc: 'Save your favorite filter combos, then one-click recall + bulk-import the top 25 contacts in any city straight from Seamless.AI\'s 1.9B+ database.', span: '' },
  { icon: BarChart3, title: 'Analytics & Reports', desc: 'Track conversion rates, lead sources, and team performance with real-time dashboards.', span: '' },
  { icon: Zap, title: 'Automations', desc: 'Auto-assign leads, trigger email sequences, and set up webhooks for seamless workflows.', span: '' },
  { icon: Calendar, title: 'Appointments', desc: 'Schedule meetings, set reminders, and sync with your calendar. Never miss a follow-up.', span: '' },
  { icon: Shield, title: 'A2P 10DLC Compliance Suite', desc: 'Built-in SMS opt-in disclosures, consent audit log, monthly compliance reports, resend confirmations, and a live Health Score widget — pass carrier review without a lawyer.', span: 'md:col-span-2' },
  { icon: Shield, title: 'Role-Based Access', desc: 'Super Admin, Admin, Member, and Viewer roles ensure the right people have the right access.', span: 'md:col-span-2' },
];

const TESTIMONIALS = [
  { name: 'Marcus Johnson', role: 'Managing Partner, Apex Financial Group', quote: 'Real Leads CRM transformed how our team shares opportunities. We closed 40% more deals in the first quarter.', initials: 'MJ', color: '#007AFF' },
  { name: 'Priya Desai', role: 'Director of Sales, BrightPath Consulting', quote: 'The simplicity is what sold us. No bloated features — just clean lead management and sharing that actually works.', initials: 'PD', color: '#059669' },
  { name: 'David Thornton', role: 'CEO, Summit Realty Partners', quote: 'We tried three CRMs before this one. The collaboration features and analytics dashboard give us visibility we never had.', initials: 'DT', color: '#D97706' },
];

const PRICING_FEATURES = [
  'Unlimited lead creation & management',
  'Share leads with unlimited members',
  'Kanban pipeline & bulk operations',
  'Real-time team messaging + reactions',
  '1:1 video calls between members',
  'Google Places address autocomplete + satellite maps',
  'Email campaigns & sequences',
  'Calendar & appointments',
  'Real-time analytics dashboard',
  'AI-powered support chat',
  'CSV import/export & printing',
  'Role-based access control',
  'A2P 10DLC compliance suite (audit log, monthly reports, health score)',
  'Priority email support',
];

export default function LandingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const abVariant = useABVariant();
  const heroVideoRef = useRef(null);
  const [heroMuted, setHeroMuted] = useState(true);

  const toggleHeroSound = () => {
    if (heroVideoRef.current) {
      heroVideoRef.current.muted = !heroVideoRef.current.muted;
      setHeroMuted(heroVideoRef.current.muted);
    }
  };

  useEffect(() => {
    if (user) navigate('/dashboard');
  }, [user, navigate]);

  const handleCtaClick = () => {
    if (abVariant) {
      api.trackABEvent('hero_cta', abVariant, 'click').catch(err => logger.error(err));
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="landing-page">
      <Seo path="/" />
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-[#0A0A0A]/70 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex items-center justify-between h-16">
          <LogoWhite size="sm" />
          <div className="flex items-center gap-1 sm:gap-4">
            <Link to="/pricing" className="hidden sm:inline-block">
              <Button variant="ghost" className="text-sm text-zinc-400 hover:text-white transition-colors" data-testid="header-pricing-btn">Pricing</Button>
            </Link>
            <Link to="/login">
              <Button variant="ghost" className="text-sm text-zinc-400 hover:text-white transition-colors" data-testid="header-login-btn">Sign In</Button>
            </Link>
            <Link to="/register">
              <Button className="text-sm bg-[#007AFF] hover:bg-[#005bb5] text-white rounded-full px-5 transition-colors" data-testid="nav-register-button">Get Started</Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero — Full viewport with video background */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <video
          ref={heroVideoRef}
          className="absolute inset-0 w-full h-full object-cover"
          src="/hero_with_audio.mp4"
          autoPlay muted loop playsInline
          data-testid="hero-video"
        />
        <div className="absolute inset-0 bg-black/60" />
        {/* Sound toggle */}
        <button
          onClick={toggleHeroSound}
          className="absolute top-20 right-6 z-20 w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center hover:bg-white/20 transition-colors"
          data-testid="hero-sound-toggle"
          aria-label={heroMuted ? 'Unmute' : 'Mute'}
        >
          {heroMuted ? <VolumeX className="w-4 h-4 text-white/70" /> : <Volume2 className="w-4 h-4 text-white" />}
        </button>
        <motion.div
          className="relative z-10 max-w-4xl mx-auto px-6 text-center"
          initial="hidden" animate="visible"
          variants={{ visible: { transition: { staggerChildren: 0.15 } } }}
        >
          <motion.p variants={fadeUp} transition={{ duration: 0.6 }} className="text-xs font-bold tracking-[0.25em] uppercase text-[#007AFF] mb-6" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Collaborative Lead Management</motion.p>
          <motion.h1 variants={fadeUp} transition={{ duration: 0.7 }} className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tighter leading-tight mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Share leads.<br />Close deals.<br />
            <span className="text-[#007AFF]">Grow together.</span>
          </motion.h1>
          <motion.p variants={fadeUp} transition={{ duration: 0.6 }} className="text-base sm:text-lg text-zinc-400 max-w-2xl mx-auto mb-6 leading-relaxed" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            The CRM built for teams that share opportunities. Create, manage, and share leads with your entire network.
          </motion.p>
          <motion.div variants={fadeUp} transition={{ duration: 0.5 }} className="inline-flex items-center gap-2 mb-8 px-3.5 py-1.5 bg-white/5 border border-white/10 rounded-full" data-testid="hero-price-tag">
            <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-emerald-400">Save 28%</span>
            <span className="w-1 h-1 rounded-full bg-white/30" />
            <span className="text-xs text-zinc-300 font-medium">Starting at <span className="text-white font-semibold">$25</span><span className="text-zinc-500">/seat/mo for 6+ teams</span></span>
          </motion.div>
          <motion.div variants={fadeUp} transition={{ duration: 0.6 }} className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/register" onClick={handleCtaClick}>
              <Button className="bg-[#007AFF] hover:bg-[#005bb5] text-white rounded-full px-8 h-12 text-sm font-semibold transition-all hover:shadow-[0_0_30px_rgba(0,122,255,0.3)]" data-testid="hero-cta-btn">
                {abVariant ? AB_VARIANTS[abVariant].cta : 'Get Started'} <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
            <Link to="/login" className="text-sm font-medium text-zinc-500 hover:text-white transition-colors">
              {abVariant ? AB_VARIANTS[abVariant].subtext : 'Already a member?'}
            </Link>
          </motion.div>
        </motion.div>
        {/* Gradient fade at bottom */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#0A0A0A] to-transparent" />
      </section>

      {/* Features — Bento Grid */}
      <section className="py-24 sm:py-32" id="features">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.p initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={fadeUp} transition={{ duration: 0.5 }} className="text-xs font-bold tracking-[0.25em] uppercase text-zinc-500 mb-4">Features</motion.p>
          <motion.h2 initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={fadeUp} transition={{ duration: 0.6, delay: 0.1 }} className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight mb-16" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Everything you need<br />to <span className="text-[#007AFF]">manage leads</span>
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {FEATURES.map((f, i) => (
              <motion.div
                key={f.title}
                initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }}
                variants={scaleUp}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className={`bg-[#141414] border border-white/10 rounded-2xl p-8 hover:-translate-y-1 transition-transform duration-300 ${f.span}`}
                data-testid={`feature-card-${f.title}`}
              >
                <div className="w-11 h-11 bg-[#007AFF]/10 rounded-xl flex items-center justify-center mb-5">
                  <f.icon className="w-5 h-5 text-[#007AFF]" />
                </div>
                <h3 className="font-semibold text-white mb-2 text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>{f.title}</h3>
                <p className="text-sm text-zinc-400 leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Product Demo Video */}
      <section className="py-24 sm:py-32 bg-[#141414]" id="demo">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={fadeUp} transition={{ duration: 0.6 }} className="text-center mb-12">
            <p className="text-xs font-bold tracking-[0.25em] uppercase text-zinc-500 mb-4">See it in action</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Built for teams that <span className="text-[#007AFF]">close deals</span>
            </h2>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }} variants={scaleUp} transition={{ duration: 0.7, delay: 0.15 }}>
            <DemoVideoPlayer />
          </motion.div>
        </div>
      </section>

      {/* Testimonials — Split layout */}
      <section className="py-24 sm:py-32" id="testimonials" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left: Video */}
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }} variants={scaleUp} transition={{ duration: 0.7 }}>
              <TestimonialVideoPlayer />
            </motion.div>

            {/* Right: Written quotes */}
            <div>
              <motion.p initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={fadeUp} transition={{ duration: 0.5 }} className="text-xs font-bold tracking-[0.25em] uppercase text-zinc-500 mb-4">Testimonials</motion.p>
              <motion.h2 initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={fadeUp} transition={{ duration: 0.6, delay: 0.1 }} className="text-3xl sm:text-4xl font-semibold tracking-tight mb-10" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Trusted by teams<br />who <span className="text-[#007AFF]">close deals</span>
              </motion.h2>
              <div className="space-y-6">
                {TESTIMONIALS.map((t, i) => (
                  <motion.div
                    key={t.name}
                    initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }}
                    variants={fadeUp}
                    transition={{ duration: 0.5, delay: 0.15 + i * 0.1 }}
                    className="bg-[#141414] border border-white/10 rounded-2xl p-6 hover:border-white/20 transition-colors"
                    data-testid={`testimonial-card-${t.initials}`}
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: t.color }}>
                        <span className="text-white text-xs font-bold tracking-wide">{t.initials}</span>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-white">{t.name}</p>
                        <p className="text-[11px] text-zinc-500">{t.role}</p>
                      </div>
                    </div>
                    <p className="text-sm text-zinc-400 leading-relaxed italic">"{t.quote}"</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-24 sm:py-32 bg-[#141414]" id="pricing" data-testid="pricing-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={fadeUp} transition={{ duration: 0.6 }} className="text-center mb-12">
            <p className="text-xs font-bold tracking-[0.25em] uppercase text-zinc-500 mb-4">Pricing</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Per-seat pricing that <span className="text-[#007AFF]">scales with you.</span>
            </h2>
            <p className="text-base text-zinc-400 max-w-2xl mx-auto">
              Solo to enterprise — pay less per seat as your team grows.
            </p>
          </motion.div>
          <div className="mb-12">
            <SeatCalculator theme="dark" />
          </div>
          <PricingTiers theme="dark" />
        </div>
      </section>

      {/* FAQ */}
      <FaqSection />

      {/* Final CTA */}
      <section className="py-24 sm:py-32">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={{ visible: { transition: { staggerChildren: 0.12 } } }} className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <motion.h2 variants={fadeUp} transition={{ duration: 0.6 }} className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Ready to close more deals?
          </motion.h2>
          <motion.p variants={fadeUp} transition={{ duration: 0.5 }} className="text-lg text-zinc-400 max-w-xl mx-auto mb-10">
            Join teams that are already sharing leads and growing together with Real Leads CRM.
          </motion.p>
          <motion.div variants={fadeUp} transition={{ duration: 0.5 }}>
            <Link to="/register" onClick={handleCtaClick}>
              <Button className="bg-[#007AFF] hover:bg-[#005bb5] text-white rounded-full px-10 h-14 text-base font-semibold transition-all hover:shadow-[0_0_40px_rgba(0,122,255,0.3)]" data-testid="final-cta-btn">
                {abVariant ? AB_VARIANTS[abVariant].cta : 'Get Started'} <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 pt-12 pb-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 space-y-8">
          {/* A2P 10DLC SMS Opt-In Disclosure */}
          <div
            className="bg-white/[0.02] border border-white/10 rounded-2xl p-6 sm:p-8"
            data-testid="sms-optin-disclosure"
          >
            <div className="flex items-center gap-2 mb-3">
              <span className="inline-flex items-center justify-center w-8 h-8 rounded-md bg-[#007AFF]/10 text-[#007AFF]">
                {/* simple chat icon (no emoji) */}
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
              </span>
              <h3 className="font-heading text-sm font-semibold text-white tracking-wide uppercase">
                SMS Messaging Consent &amp; Disclosure
              </h3>
            </div>
            <p className="text-xs text-zinc-400 leading-relaxed">
              By providing your mobile phone number to Real Leads CRM (operated by Encore Business
              Services Group LLC), you expressly consent to receive recurring transactional and
              conversational SMS text messages from us, including account notifications, appointment
              reminders, lead-share alerts, two-factor authentication codes, and platform updates.
              Consent is <span className="text-zinc-200 font-medium">not a condition of purchase</span>.
              Message frequency varies. <span className="text-zinc-200 font-medium">Message and data rates may apply</span>.
              Reply <span className="text-zinc-200 font-mono font-medium">STOP</span> at any time to
              unsubscribe or <span className="text-zinc-200 font-mono font-medium">HELP</span> for
              support. <span className="text-zinc-200 font-medium">
                No mobile information will be shared with third parties or affiliates for marketing or
                promotional purposes.
              </span>{' '}
              See our{' '}
              <Link to="/sms-terms" className="text-[#007AFF] hover:underline" data-testid="footer-sms-terms-link">
                SMS Terms
              </Link>{' '}
              and{' '}
              <Link to="/privacy" className="text-[#007AFF] hover:underline" data-testid="footer-privacy-link">
                Privacy Policy
              </Link>{' '}
              for details.
            </p>
          </div>

          {/* Bottom bar */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <LogoWhite size="xs" />
            <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs text-zinc-500">
              <Link to="/sms-terms" className="hover:text-zinc-300 transition-colors">SMS Terms</Link>
              <Link to="/privacy" className="hover:text-zinc-300 transition-colors">Privacy</Link>
              <Link to="/pricing" className="hover:text-zinc-300 transition-colors">Pricing</Link>
            </div>
            <p className="text-xs text-zinc-600">&copy; {new Date().getFullYear()} Encore Business Services Group LLC. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
